<template>
  <div :style="active ? 'z-index:128;' : ''" class="form-floating relation-select-container h-100">
    <div class="form-control invisible"></div>
    <div
      :id="name + '-select'"
      :class="{ active, disabled: readonly || disabled }"
      class="position-absolute w-100 relation-select h-100"
      @click="!readonly && !disabled ? openDropdown($event) : null"
    >
      <div
        :class="{ error: error ? 'is-invalid' : '', disabled: readonly || disabled }"
        class="flex ml-2 bg-transparent form-select position-relative text-truncate h-100"
      >
        <div v-if="null" class="position-absolute top-0 end-0 remove-relation-container">
          <i
            class="bi bi-x-circle-fill text-muted remove-relation"
            style="font-size: 0.75rem"
            @click="setRelation(null)"
          ></i>
        </div>
        <div v-if="value" class="d-flex text-nowrap align-items-baseline position-absolute addressSelect">
          <span class="relation-title text-truncate">{{ localRelationTitle }}</span>
          <span class="relation-id ps-1 pe-3 smaller text-muted">(ID: {{ localValue }})</span>
        </div>
      </div>
      <div
        v-if="!readonly && !disabled"
        :class="active ? '' : ' d-none'"
        class="form-control input-container position-absolute"
      >
        <div class="input-group">
          <div class="form-floating filter-input" style="width: calc(100% - 42px)">
            <form-input
              :id="name + '-search'"
              v-model="search"
              class="form-control border-start-0 border-end-0 border-top-0"
              type="text"
              style="border-bottom-left-radius: 0"
              @keyup="searchModels($event.target.value)"
            />
          </div>
          <div class="input-group-append" @click="searchModels()">
            <span class="input-group-text h-100 border-end-0 border-top-0" style="border-bottom-right-radius: 0">
              <i class="fa fa-search"></i>
            </span>
          </div>
        </div>
        <div class="relation-results">
          <ul class="list-unstyled m-0">
            <li class="px-2" @click="setRelation(null)">
              <div class="py-1 font-italic remove-relation">zurücksetzen</div>
            </li>
            <li v-for="(model, key) in models.data" :key="key" class="px-2" @click="setRelation(model.data.id)">
              <span class="pe-0 relation-title text-truncate">{{ model.data.attributes.model_title }}</span>
              <span class="relation-id ps-1 pe-3 smaller text-muted">(ID: {{ model.data.id }})</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <form-input
      :id="id"
      v-model="localValue"
      :class="{ 'is-invalid': error }"
      :disabled="disabled"
      :name="name"
      :placeholder="label"
      :readonly="readonly"
      :required="required"
      :type="'hidden'"
      :value="localValue"
      class="form-control"
    />

    <div v-if="error" class="invalid-feedback" role="alert">
      <strong>{{ error }}</strong>
    </div>
    <label :for="name" :class="{ 'has-content': localValue }" style="z-index: 99">
      {{ labelAttribute }}
    </label>
  </div>
</template>

<script>
import FormInput from '@kanrisha/components/Elements/Form/TextInput';
import useModels from '@kanrisha/composables/useModels';
import { ref } from 'vue';

export default {
  components: {
    FormInput,
  },
  props: {
    id: { type: String, default: null },
    error: { type: String, default: null },
    name: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    label: { type: String, default: '' },
    options: { type: Object, default: () => {} },
    relationType: { type: String, default: 'model' },
    relationTitle: { type: String, default: '' },
    value: { type: [String, Number], default: '' },
    setFilter: { type: Object, default: () => ({}) },
  },
  emits: ['update:data'],
  setup(props, { emit }) {
    const { models, fetchModels, fetchModel, currentModel } = useModels({
      resourceType: props.relationType,
    });

    const sort = ref('');
    const filter = ref(props.setFilter);
    const active = ref(null);
    const search = ref('');
    const localValue = ref(props.value);
    const localRelationTitle = ref(props.relationTitle);
    const readonly = ref(props.options?.readonly);

    const searchModels = async (searchValue) => {
      filter.value.search = searchValue ?? 'RESET';
      fetchModels(
        {
          sort: sort.value,
          filter: filter.value,
          relation: props.options?.relation ?? null,
          parent: props.options?.parent ?? null,
        },
        'volatile'
      );
    };

    const openDropdown = (e) => {
      if (e.target.classList.contains('remove-relation')) {
        setRelation(null);
        let el = e.target;
        while (!el.classList.contains('relation-select') && el !== el.parentElement) {
          el = el.parentElement;
        }
        document.removeEventListener('click', el.clickOutsideEvent);
        return;
      }

      let el = e.target;
      while (!el.classList.contains('relation-select') && el !== el.parentElement) {
        el = el.parentElement;
      }

      if (el.id !== active.value) {
        el.clickOutsideEvent = async (event) => {
          await searchModels('');
          let target = event.target;
          while (!target && !target.classList.contains('form-select') && target !== target.parentElement) {
            target = target.parentElement;
          }
          if (
            (!el.contains(event.target) && !target.classList.contains('form-select')) ||
            (target.parentElement.id && active.value !== target.parentElement.id)
          ) {
            active.value = null;
            document.removeEventListener('click', el.clickOutsideEvent);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
        active.value = el.id;
      } else if (e.target.classList.contains('form-select') || e.target.classList.contains('text-truncate')) {
        active.value = null;
      }
    };

    const setRelation = async (id) => {
      if (id) {
        await fetchModel(id);
      } else {
        currentModel.value = null;
      }

      localValue.value = id;
      document.getElementById(props.id).dispatchEvent(new Event('change'));
      active.value = false;
      localRelationTitle.value = currentModel.value?.data.attributes.model_title ?? '';
      emit('update:data', id, props.name, localRelationTitle.value, currentModel.value);
    };

    return {
      active,
      localValue,
      localRelationTitle,
      models,
      openDropdown,
      readonly,
      searchModels,
      search,
      setRelation,
    };
  },
  computed: {
    labelAttribute() {
      return this.required ? `${this.label} *` : this.label;
    },
  },
};
</script>
<style lang="scss">
.addressSelect {
  width: 93%;
  height: 100%;
}

.addressSelect span:first-child {
  padding-right: 2rem !important;
}

@media screen and (max-width: 991px) {
  .addressSelect span:first-child {
    padding-right: 4rem !important;
  }
}
</style>
